/**
 * VoicenotesPage.jsx — Voice Notes: simple chronological checklist.
 *
 * One list of voice notes, newest first, with a checkbox to mark each done.
 * Done notes collapse into a section below and can be unchecked to restore.
 * (The Trello-style multi-board kanban lives in git history at 4e079d8.)
 *
 * To-dos: "Make to-do" opens the new to-do dialog prefilled from the note (the
 * note itself is not changed). Expanded, a note lists its action items, each with
 * a one-click Add (see todoText.voiceNoteActionItems for how items are keyed).
 */
import { useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Checkbox, IconButton, Chip,
  Collapse, Divider, Stack, Tooltip, TextField, CircularProgress, Alert, Button,
} from '@mui/material';
import {
  Mic as MicIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Check as CheckIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowRight as ArrowRightIcon,
  RocketLaunch as PlanIcon,
  Checklist as TodoIcon,
} from '@mui/icons-material';
import { useVoicenotes, useUpdateVoicenote, useDoneVoicenote, useQueueVoicenote } from '../hooks/useVoicenotes';
import { useCreateTodo, useVoicenoteTodoItems, newTodoId } from '../hooks/useTodos';
import { voiceNoteActionItems } from '../lib/todoText';
import { useToast } from '../components/layout/Toast';
import TodoDialog from '../components/todos/TodoDialog';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const chipSx = { height: { xs: 28, sm: 22 }, fontSize: '0.7rem', fontWeight: 600 };

function NoteRow({ note, onToggleDone, onToggleQueue, onUpdate, onMakeTodo, todoItems, onAddItem, addingKey }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [notesVal, setNotesVal] = useState(note.notes || '');
  const [saved, setSaved] = useState(false);
  const saveTimer = useRef(null);
  const done = note.status === 'done';
  const summary = note.triage?.summary;
  const actionItems = useMemo(() => voiceNoteActionItems(note), [note]);

  const saveNotes = (val) => {
    if (val === (note.notes || '')) return;
    onUpdate({ id: note.id, notes: val });
    setSaved(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => setSaved(false), 1500);
  };

  return (
    <Card variant="outlined" sx={{ borderLeft: `3px solid ${done ? '#9CA3AF' : '#F97316'}`, opacity: done ? 0.65 : 1 }}>
      <CardContent sx={{ py: 1, px: 1.25, '&:last-child': { pb: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          <Checkbox
            checked={done}
            onChange={() => onToggleDone(note)}
            disabled={note.status === 'processing'}
            size="small"
            sx={{ p: { xs: 0.75, sm: 0.25 }, mt: 0.1, color: '#F97316', '&.Mui-checked': { color: '#22C55E' } }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, lineHeight: 1.35, textDecoration: done ? 'line-through' : 'none' }}
            >
              {note.title || '(Untitled)'}
            </Typography>
            {summary && !done && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {summary}
              </Typography>
            )}
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {!done && (
                <Chip
                  icon={note.queuedAt
                    ? <CheckIcon sx={{ fontSize: '13px !important' }} />
                    : <PlanIcon sx={{ fontSize: '13px !important' }} />}
                  label={note.queuedAt ? 'Queued' : 'Plan in chat'}
                  size="small"
                  clickable
                  onClick={() => onToggleQueue(note)}
                  sx={{
                    ...chipSx,
                    bgcolor: note.queuedAt ? '#22C55E22' : '#F9731615',
                    color: note.queuedAt ? '#22C55E' : '#F97316',
                    '& .MuiChip-icon': { color: note.queuedAt ? '#22C55E' : '#F97316' },
                    '&:hover': { bgcolor: note.queuedAt ? '#22C55E38' : '#F9731630' },
                  }}
                />
              )}
              {!done && (
                <Chip
                  data-track="voicenotes.todo.make"
                  icon={<TodoIcon sx={{ fontSize: '13px !important' }} />}
                  label="Make to-do"
                  size="small"
                  clickable
                  onClick={() => onMakeTodo(note)}
                  sx={{
                    ...chipSx,
                    bgcolor: '#38BDF815',
                    color: '#0284C7',
                    '& .MuiChip-icon': { color: '#0284C7' },
                    '&:hover': { bgcolor: '#38BDF830' },
                  }}
                />
              )}
              {!expanded && actionItems.length > 0 && (
                <Chip
                  data-track="voicenotes.todo.items"
                  label={`${actionItems.length} action ${actionItems.length === 1 ? 'item' : 'items'}`}
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={() => setExpanded(true)}
                  sx={{ ...chipSx, fontWeight: 500 }}
                />
              )}
              {note.status === 'processing' && (
                <Chip icon={<CircularProgress size={12} />} label="Triaging..." size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
              )}
              {note.status === 'error' && (
                <Chip label="Error" size="small" color="error" sx={{ fontSize: '0.7rem', height: 20 }} />
              )}
              <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                {done && note.completedAt ? `done ${timeAgo(note.completedAt)}` : timeAgo(note.createdAt)}
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            <Tooltip title={expanded ? 'Collapse' : 'Transcript'}>
              <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: { xs: 0.75, sm: 0.25 } }}>
                {expanded ? <CollapseIcon sx={{ fontSize: 18 }} /> : <ExpandIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Archive">
              <IconButton
                size="small"
                onClick={() => { if (window.confirm('Archive this voice note?')) onUpdate({ id: note.id, archived_at: new Date().toISOString() }); }}
                sx={{ color: 'error.main', opacity: { xs: 0.7, sm: 0.45 }, p: { xs: 0.75, sm: 0.25 }, '&:hover': { opacity: 1 } }}
              >
                <DeleteIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ my: 1 }} />
          {note.transcript && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Transcript
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap',
                  fontStyle: 'italic', color: 'text.secondary', fontSize: '0.82rem', lineHeight: 1.6,
                }}
              >
                {note.transcript}
              </Typography>
            </Box>
          )}
          {actionItems.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Action items
              </Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {actionItems.map((item) => {
                  const made = todoItems?.get(item.key);
                  return (
                    <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 44 }}>
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontSize: '0.82rem', overflowWrap: 'anywhere' }}>
                        {item.text}
                      </Typography>
                      {made ? (
                        <Chip
                          data-track="voicenotes.todo.open"
                          icon={<CheckIcon sx={{ fontSize: '13px !important' }} />}
                          label={made.done ? 'Done' : 'Added'}
                          size="small"
                          clickable
                          onClick={() => navigate(`/todos?todo=${encodeURIComponent(made.todoId)}`)}
                          sx={{ ...chipSx, bgcolor: '#22C55E22', color: '#16A34A', '& .MuiChip-icon': { color: '#16A34A' } }}
                        />
                      ) : (
                        <Button
                          data-track="voicenotes.todo.add"
                          size="small"
                          variant="outlined"
                          onClick={() => onAddItem(note, item)}
                          disabled={!!addingKey}
                          sx={{ textTransform: 'none', minHeight: { xs: 44, sm: 32 }, minWidth: 0, px: 1.5, flexShrink: 0 }}
                        >
                          {addingKey === `${note.id}|${item.key}` ? 'Adding...' : 'Add'}
                        </Button>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Your Notes
              </Typography>
              {saved && <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />}
            </Box>
            <TextField
              multiline
              minRows={2}
              maxRows={8}
              fullWidth
              size="small"
              placeholder="Add context or capture decisions..."
              value={notesVal}
              onChange={(e) => setNotesVal(e.target.value)}
              onBlur={() => saveNotes(notesVal)}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem', bgcolor: 'background.paper' } }}
            />
          </Box>
          {note.status === 'error' && note.error && (
            <Alert severity="error" sx={{ mt: 1, fontSize: '0.8rem' }}>{note.error}</Alert>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default function VoicenotesPage() {
  const { data: notes = [], isLoading } = useVoicenotes();
  const updateVN = useUpdateVoicenote();
  const doneVN = useDoneVoicenote();
  const queueVN = useQueueVoicenote();
  const createTodo = useCreateTodo();
  const { toast } = useToast();
  const [showDone, setShowDone] = useState(false);
  const [todoDraft, setTodoDraft] = useState(null);
  const [addingKey, setAddingKey] = useState(null);

  // Server returns newest → oldest (created_at DESC), archived + plan-linked
  // hidden. The archivedAt check covers the optimistic window before refetch.
  const active = notes.filter((n) => n.status !== 'done' && !n.archivedAt);
  const doneNotes = notes
    .filter((n) => n.status === 'done' && !n.archivedAt)
    .sort((a, b) => new Date(b.completedAt || b.updatedAt || 0) - new Date(a.completedAt || a.updatedAt || 0));

  const toggleDone = useCallback((note) => {
    if (note.status === 'done') {
      const restored = (note.triage && Object.keys(note.triage).length) ? 'triaged'
        : note.error ? 'error' : 'received';
      updateVN.mutate({ id: note.id, status: restored, completed_at: null });
    } else {
      doneVN.mutate(note.id);
    }
  }, [updateVN, doneVN]);

  const handleUpdate = useCallback((fields) => updateVN.mutate(fields), [updateVN]);

  // Which action items already have a to-do (mine): note id -> Map(item key -> { todoId, done }).
  const noteIdsWithItems = useMemo(
    () => notes.filter((n) => !n.archivedAt && voiceNoteActionItems(n).length).map((n) => n.id),
    [notes],
  );
  const { data: vnTodos } = useVoicenoteTodoItems(noteIdsWithItems);
  const todoItemsByNote = useMemo(() => {
    const byNote = new Map();
    (vnTodos?.items || []).forEach((r) => {
      if (!byNote.has(r.sourceId)) byNote.set(r.sourceId, new Map());
      byNote.get(r.sourceId).set(r.sourceItem, { todoId: r.todoId, done: r.done });
    });
    return byNote;
  }, [vnTodos]);

  const makeTodo = useCallback((note) => setTodoDraft({
    title: note.title || '(Untitled)',
    notes: note.triage?.summary || '',
    source: 'voicenote',
    sourceId: note.id,
  }), []);

  const addItem = useCallback(async (note, item) => {
    setAddingKey(`${note.id}|${item.key}`);
    try {
      await createTodo.mutateAsync({
        id: newTodoId(),
        title: item.text.slice(0, 300),
        source: 'voicenote',
        sourceId: note.id,
        sourceItem: item.key,
      });
      toast('Added to your to-dos.');
    } catch (err) {
      toast(err?.message || 'Could not add it. Try again.', 'error');
    } finally {
      setAddingKey(null);
    }
  }, [createTodo, toast]);

  // Queue = "plan in chat": Claude reads queued notes at session start and
  // plans how to tackle them. Un-queue via plain PUT (DELETE /queue marks done).
  const toggleQueue = useCallback((note) => {
    if (note.queuedAt) updateVN.mutate({ id: note.id, queued_at: null });
    else queueVN.mutate(note.id);
  }, [updateVN, queueVN]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1, maxWidth: 760, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <MicIcon sx={{ color: '#F97316', fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Voice Notes</Typography>
        <Typography variant="body2" color="text.disabled" sx={{ ml: 'auto' }}>
          {active.length} open{active.some((n) => n.queuedAt) ? ` · ${active.filter((n) => n.queuedAt).length} queued for chat` : ''}
        </Typography>
      </Box>

      <Stack spacing={1}>
        {active.map((n) => (
          <NoteRow
            key={n.id}
            note={n}
            onToggleDone={toggleDone}
            onToggleQueue={toggleQueue}
            onUpdate={handleUpdate}
            onMakeTodo={makeTodo}
            todoItems={todoItemsByNote.get(n.id)}
            onAddItem={addItem}
            addingKey={addingKey}
          />
        ))}
        {active.length === 0 && (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 4 }}>
            No open voice notes.
          </Typography>
        )}
      </Stack>

      {doneNotes.length > 0 && (
        <Box sx={{ mt: 2.5, mb: 4 }}>
          <Box
            onClick={() => setShowDone((v) => !v)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: 1, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            {showDone ? <ArrowDownIcon sx={{ fontSize: 20 }} /> : <ArrowRightIcon sx={{ fontSize: 20 }} />}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Done ({doneNotes.length})
            </Typography>
          </Box>
          <Collapse in={showDone}>
            <Stack spacing={1}>
              {doneNotes.map((n) => (
                <NoteRow
                  key={n.id}
                  note={n}
                  onToggleDone={toggleDone}
                  onToggleQueue={toggleQueue}
                  onUpdate={handleUpdate}
                  onMakeTodo={makeTodo}
                  todoItems={todoItemsByNote.get(n.id)}
                  onAddItem={addItem}
                  addingKey={addingKey}
                />
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}

      <TodoDialog open={!!todoDraft} initial={todoDraft} onClose={() => setTodoDraft(null)} />
    </Box>
  );
}
